/**
 * Coach Dashboard — the persistent "state of the trader" surface.
 *
 * Where the LiveCoachFeed and Setup/ATM cards show observations IN CONTEXT
 * (today, this setup, this ATM), this dashboard zooms out to show Coach's
 * overall mental model of the trader: current edges, current leaks, leaks
 * that resolved, and what changed since the last snapshot.
 *
 * It's the dashboard you check Sunday night before next week starts —
 * a coach's recap, not a per-trade card list.
 *
 * Props:
 *   profile      CoachProfile | null  — current profile from useCoachProfile
 *   diff         ProfileDiff | null   — drift since last snapshot
 *   isLoading    bool                 — true while rebuild is in flight
 *   error        string | null
 *   onRebuild    () => Promise<void>  — trigger a fresh profile build
 *   onClearMemory () => Promise<void> — wipe stored profile + history
 *   onClose      () => void
 *   setupCount   number               — scopes the rebuild covered
 *   atmCount     number
 *   darkMode     bool
 */

import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Sparkles, RefreshCw, Trash2, TrendingUp, TrendingDown,
  CheckCircle2, AlertTriangle, ArrowUpRight, ArrowDownRight, Brain,
  Activity, Flame, Clock, MessageSquare, Loader2,
} from 'lucide-react';
import { summarizeDiff } from '../../services/coach/profile/diffProfile';
import { computeHealthScore } from '../../services/coach/health/computeHealthScore';
import CoachHealthScore from './CoachHealthScore';
import CoachSuggestionsPanel from './CoachSuggestionsPanel';
import CoachSpeakButton from './CoachSpeakButton';

const SEVERITY_TO_ACCENT = {
  success:  { dot: 'bg-emerald-400', border: 'border-l-emerald-500', text: 'text-emerald-300' },
  warning:  { dot: 'bg-amber-400',   border: 'border-l-amber-500',   text: 'text-amber-300'   },
  critical: { dot: 'bg-red-400',     border: 'border-l-red-500',     text: 'text-red-300'     },
  info:     { dot: 'bg-sky-400',     border: 'border-l-sky-500',     text: 'text-sky-300'     },
};

const fmtRelative = (iso) => {
  if (!iso) return '';
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const days = Math.round((now - then) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.round(days / 7)}w ago`;
    return `${Math.round(days / 30)}mo ago`;
  } catch { return ''; }
};

const fmtAbsolute = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return ''; }
};

// ────────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────────

// StatTile — frosted tinted glass surface matching the v7.6 SlicerBar +
// "engaged filter" chip language. Tints per semantic accent (emerald = edges,
// amber = leaks, sky = resolved, purple = scope/spotlight); each gets an
// inset ring + soft color-keyed glow on hover so it reads as a glass card
// rather than a flat colored panel.
const StatTile = ({ icon: Icon, label, value, sublabel, accent = 'sky', darkMode }) => {
  const accentColor = {
    emerald: darkMode
      ? 'text-emerald-100 bg-emerald-500/15 border-emerald-400/40 ring-1 ring-inset ring-emerald-400/20 hover:bg-emerald-500/20 hover:border-emerald-400/55 hover:shadow-[0_0_14px_rgba(16,185,129,0.18)]'
      : 'text-emerald-800 bg-emerald-50 border-emerald-300 ring-1 ring-inset ring-emerald-200',
    amber: darkMode
      ? 'text-amber-100 bg-amber-500/15 border-amber-400/40 ring-1 ring-inset ring-amber-400/20 hover:bg-amber-500/20 hover:border-amber-400/55 hover:shadow-[0_0_14px_rgba(245,158,11,0.18)]'
      : 'text-amber-800 bg-amber-50 border-amber-300 ring-1 ring-inset ring-amber-200',
    red: darkMode
      ? 'text-red-100 bg-red-500/15 border-red-400/40 ring-1 ring-inset ring-red-400/20 hover:bg-red-500/20 hover:border-red-400/55 hover:shadow-[0_0_14px_rgba(239,68,68,0.18)]'
      : 'text-red-800 bg-red-50 border-red-300 ring-1 ring-inset ring-red-200',
    sky: darkMode
      ? 'text-sky-100 bg-sky-500/15 border-sky-400/40 ring-1 ring-inset ring-sky-400/20 hover:bg-sky-500/20 hover:border-sky-400/55 hover:shadow-[0_0_14px_rgba(56,189,248,0.18)]'
      : 'text-sky-800 bg-sky-50 border-sky-300 ring-1 ring-inset ring-sky-200',
    purple: darkMode
      ? 'text-purple-100 bg-purple-500/15 border-purple-400/40 ring-1 ring-inset ring-purple-400/20 hover:bg-purple-500/20 hover:border-purple-400/55 hover:shadow-[0_0_14px_rgba(168,85,247,0.18)]'
      : 'text-purple-800 bg-purple-50 border-purple-300 ring-1 ring-inset ring-purple-200',
  }[accent] || 'text-sky-100 bg-sky-500/15 border-sky-400/40 ring-1 ring-inset ring-sky-400/20';

  return (
    <div className={`rounded-lg border p-3 transition-[background-color,border-color,box-shadow,color] duration-150 ${accentColor}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="opacity-80" />
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
      {sublabel && (
        <div className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {sublabel}
        </div>
      )}
    </div>
  );
};

const ProfileEntryCard = ({ entry, kind, darkMode }) => {
  const accent = SEVERITY_TO_ACCENT[entry.severity] || SEVERITY_TO_ACCENT.info;
  const persistenceLabel = entry.occurrences > 1
    ? `Seen ${entry.occurrences}× since ${fmtRelative(entry.firstSeenAt)}`
    : `New ${fmtRelative(entry.firstSeenAt)}`;
  const evidence = entry.evidenceSummary;

  return (
    <div
      className={`
        rounded-lg border-l-4 ${accent.border} p-3 transition-[background-color,border-color,box-shadow,color] duration-150
        ${darkMode ? 'bg-white/[0.04] border-y border-r border-white/10 hover:bg-white/[0.06]' : 'bg-white border-y border-r border-gray-200 hover:bg-gray-50'}
      `}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`w-2 h-2 rounded-full ${accent.dot} shrink-0`} />
          <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {entry.scope}
            {entry.scopeId && <span className="opacity-70"> · {entry.scopeId}</span>}
          </span>
        </div>
        <span className={`text-[10px] shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {persistenceLabel}
        </span>
      </div>
      <p className={`text-sm font-medium leading-snug ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {entry.title}
      </p>
      {entry.detail && (
        <p className={`text-xs leading-snug mt-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {entry.detail}
        </p>
      )}
      {entry.action && (
        <p className={`text-[11px] italic leading-snug mt-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          → {entry.action}
        </p>
      )}
      {evidence && (
        <div className={`flex flex-wrap gap-2 mt-2 text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {Object.entries(evidence).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className="opacity-70 uppercase tracking-wider">{k}</span>
              <span className="font-mono tabular-nums">
                {typeof v === 'number' && v < 1 && v > -1 && v !== 0
                  ? `${(v * 100).toFixed(0)}%`
                  : (typeof v === 'number' ? v.toFixed(2) : String(v))}
              </span>
            </span>
          ))}
        </div>
      )}
      {kind === 'resolved' && (
        <div className={`mt-2 text-[10px] flex items-center gap-1 ${darkMode ? 'text-emerald-400/80' : 'text-emerald-600/80'}`}>
          <CheckCircle2 size={11} />
          <span>Resolved {fmtRelative(entry.resolvedAt)}</span>
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, count, accent, children, emptyMessage, darkMode }) => {
  const accentText = {
    emerald: 'text-emerald-400',
    amber:   'text-amber-400',
    red:     'text-red-400',
    sky:     'text-sky-400',
    purple:  'text-purple-400',
  }[accent] || 'text-sky-400';

  return (
    <section className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={accentText} />
        <h3 className={`text-sm font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {title}
        </h3>
        {typeof count === 'number' && (
          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>· {count}</span>
        )}
      </div>
      {React.Children.count(children) > 0 ? (
        <div className="space-y-2">{children}</div>
      ) : (
        <div className={`rounded-lg border border-dashed py-4 px-3 text-center text-xs ${
          darkMode ? 'border-white/10 text-gray-500' : 'border-gray-200 text-gray-500'
        }`}>
          {emptyMessage}
        </div>
      )}
    </section>
  );
};

// We use React.Children inside Section above; need an explicit React import
// because some build configs don't auto-inject the namespace.
import React from 'react';

// ────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────

const CoachDashboard = ({
  profile,
  history = [],       // compact history snapshots for trend computation (Phase 14 health score)
  diff,
  isLoading,
  error,
  onRebuild,
  onClearMemory,
  onClose,
  setupCount = 0,
  atmCount = 0,
  accountCount = 0,
  llmNarrator,        // optional: { isReady, narrate, isLoading, settings, ... } from useLlmNarrator
  // Phase 14 — pending Coach Suggestions + handlers (controlled by parent)
  coachSuggestions = [],
  onApplyCoachSuggestion,
  onDismissCoachSuggestion,
  darkMode = true,
}) => {
  // Esc to close
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── LLM Narrator (Phase 3) ─────────────────────────────────────────
  // When the LLM narrator is enabled + an API key is present, ask Claude
  // to synthesize the profile's top edges + leaks into a coaching paragraph.
  // Falls back to "no narrator configured" copy when disabled. The result
  // caches inside llmNarrator's service so reopening the dashboard during
  // a session doesn't re-burn tokens.
  const [narrative, setNarrative] = useState({ text: '', loading: false, error: null, fromCache: false });
  const lastNarratedSignatureRef = useRef('');

  // ⚠️ Infinite-loop guard (Phase 16 hotfix): useLlmNarrator returns a fresh
  // object literal every render. If we put `llmNarrator` itself in a useEffect
  // dep array, the effect fires every render — and when the effect calls
  // setNarrative (which happens whenever isReady is false), we trigger another
  // render and recurse. Capturing the latest narrator into a ref + depending
  // only on the PRIMITIVE `isReady` flag (boolean) breaks the cycle.
  const llmNarratorRef = useRef(llmNarrator);
  useEffect(() => { llmNarratorRef.current = llmNarrator; });
  const isNarratorReady = !!llmNarrator?.isReady;

  // Mounted-state ref (Phase 16 hotfix #3) — Strict-Mode-safe.
  //
  // The naive `let cancelled = false; return () => { cancelled = true; }`
  // pattern (per-effect) cancels in-flight narrates on every re-render,
  // wedging the narrative in permanent loading. So we lifted cancellation
  // to component lifecycle via a ref. BUT a unmount-only cleanup like
  //   useEffect(() => () => { ref.current = false; }, [])
  // is BROKEN in React.StrictMode: dev's double-invocation runs the
  // cleanup once and then re-runs the effect, which doesn't reset the ref
  // back to true → ref stays false forever → async results all bail.
  //
  // Setting the ref to true inside the effect's BODY (not just the cleanup)
  // makes it Strict-Mode safe: each effect run reseats the flag.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Build a stable cache-key proxy so we only re-narrate when the profile
  // signature set actually changes (not on every dashboard re-mount).
  const profileSignature = profile?.signatures
    ? [...profile.signatures].sort().join(',')
    : '';

  useEffect(() => {
    if (!isNarratorReady) {
      // Reset to empty — but ONLY if state isn't already empty. Without this
      // guard the effect would setNarrative with a fresh object literal on
      // every parent re-render, which React treats as a state change (new
      // object identity), triggering another render → infinite loop.
      setNarrative(prev =>
        (prev.text === '' && !prev.loading && !prev.error && !prev.fromCache)
          ? prev
          : { text: '', loading: false, error: null, fromCache: false }
      );
      lastNarratedSignatureRef.current = '';
      return;
    }
    if (!profile || (!profile.edges?.length && !profile.leaks?.length)) {
      return;
    }
    if (profileSignature === lastNarratedSignatureRef.current) {
      return;  // already narrated this exact set
    }
    // Read narrator via ref so we use the LATEST narrate function without
    // putting the (unstable) llmNarrator object in deps.
    const narrator = llmNarratorRef.current;
    if (!narrator?.narrate) return;

    // ⚠️ Set the signature ref BEFORE starting the async call — not after
    // it resolves. Without this, parent re-renders during the in-flight
    // narrate (caused by unstable prop refs upstream) re-fire this effect,
    // find the ref still empty, and call setNarrative repeatedly. The
    // repeat setNarrative calls cascade into infinite renders + OOM crash.
    lastNarratedSignatureRef.current = profileSignature;
    setNarrative({ text: '', loading: true, error: null, fromCache: false });
    // Convert profile entries to observation-shaped objects so the prompt
    // builder treats them like normal observations (it reads
    // title/detail/action/severity/temporalContext, all of which the
    // profile entry already carries).
    const asObservations = [
      ...(profile.edges || []),
      ...(profile.leaks || []),
    ].map(entry => ({
      id: entry.observationId || entry.signature,
      analyzer: entry.analyzer,
      scope: entry.scope,
      scopeId: entry.scopeId,
      category: entry.category,
      severity: entry.severity,
      title: entry.title,
      detail: entry.detail,
      action: entry.action,
      priority: entry.priority,
      // Synthesize a temporalContext from the profile entry's persistence
      // metadata so the narrator can naturally weave in "4th run" mentions.
      temporalContext: entry.occurrences > 1
        ? { state: 'persistent', runs: entry.occurrences, firstSeenAt: entry.firstSeenAt }
        : { state: 'new', runs: 1, firstSeenAt: entry.firstSeenAt },
    }));
    (async () => {
      const result = await narrator.narrate({
        observations: asObservations,
        scope: 'global',
        scopeId: 'profile',
        tradeCount: profile.tradeCount || 0,
      });
      // Mounted-ref check (Phase 16 hotfix #2) — see mountedRef comment
      // above. Replaces the per-effect `cancelled` flag, which caused the
      // stuck-shimmer wedge when a re-render's cleanup cancelled the
      // in-flight call that the early-return then prevented from replacing.
      if (!mountedRef.current) return;
      if (result.ok) {
        // lastNarratedSignatureRef was already set before the async call
        // started, so we just commit the narrative text here.
        setNarrative({
          text: result.text,
          loading: false,
          error: null,
          fromCache: !!result.fromCache,
        });
      } else {
        // On error, reset the ref so the next render retries rather than
        // wedging in a permanent "already tried" state.
        lastNarratedSignatureRef.current = '';
        setNarrative({ text: '', loading: false, error: result.error, fromCache: false });
      }
    })();
    // Stable primitive deps only — `isNarratorReady` is a boolean and
    // `profileSignature` is a string. Object/function refs from llmNarrator
    // are read via the ref above.
    // NOTE: no cleanup returned — see the mounted-ref comment above.
  }, [isNarratorReady, profile, profileSignature]);

  const hasProfile = !!profile;
  const driftSummary = diff ? summarizeDiff(diff) : 'No profile built yet — click Refresh to scan your data.';

  // Resolved leaks: prefer diff.resolvedLeaks (post-comparison) but fall
  // back to profile.resolvedLeaks (built-in). They should be identical
  // most of the time.
  const resolvedLeaks = (diff?.resolvedLeaks?.length ? diff.resolvedLeaks : profile?.resolvedLeaks) || [];
  const newEdges = diff?.newEdges || [];
  const newLeaks = diff?.newLeaks || [];
  const persistedEdges = diff?.persistedEdges || [];
  const persistedLeaks = diff?.persistedLeaks || [];
  const ascended = diff?.ascended || [];
  const regressed = diff?.regressed || [];

  const handleClearMemory = async () => {
    if (!profile) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    const ok = typeof window !== 'undefined' && window.confirm
      ? window.confirm('Wipe all Coach memory (current profile + history)? This cannot be undone.')
      : true;
    // Defensive focus reset for Electron — window.confirm steals focus.
    if (typeof document !== 'undefined') document.body.focus();
    if (ok) await onClearMemory?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Canonical v7.6 frosted-glass modal — bg-gray-950/90 + backdrop-blur-2xl
          + border-white/10 + rounded-2xl. Matches the language used by every
          other modal in the app (AI Insights, Add Trade, JournifiSync, etc.) */}
      <div
        className={`
          relative w-full max-w-5xl max-h-[90vh] flex flex-col
          rounded-2xl shadow-2xl backdrop-blur-2xl
          ${darkMode ? 'bg-gray-950/90 border border-white/10' : 'bg-white/90 border border-gray-200'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className={`flex items-start justify-between gap-3 p-5 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 min-w-0">
            {/* Brain icon chip — matches v7.6 purple glass language with ring */}
            <div className={`rounded-lg p-2 border shrink-0 ${
              darkMode
                ? 'bg-purple-500/20 border-purple-400/40 ring-1 ring-inset ring-purple-400/20'
                : 'bg-purple-50 border-purple-300 ring-1 ring-inset ring-purple-200'
            }`}>
              <Brain size={20} className={darkMode ? 'text-purple-200' : 'text-purple-700'} />
            </div>
            <div className="min-w-0">
              <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Coach Dashboard
              </h2>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {hasProfile
                  ? `Built ${fmtAbsolute(profile.generatedAt)} · ${fmtRelative(profile.generatedAt)}`
                  : 'No profile yet — Coach has no persistent memory of you yet.'}
              </p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {driftSummary}
              </p>
            </div>
          </div>
          {/* Header action buttons — v7.6 frosted glass via shared .btn-glass
              utility classes. Refresh = purple (matches AI Insights family),
              Clear Memory = red (destructive), Close = neutral icon button
              with subtle hover. */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onRebuild}
              disabled={isLoading}
              className={`btn-glass btn-glass-purple inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${isLoading ? 'cursor-wait' : ''}`}
              title="Re-analyze every setup + ATM and rebuild the profile"
            >
              <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
              {isLoading ? 'Building…' : (hasProfile ? 'Refresh' : 'Build Profile')}
            </button>
            {hasProfile && (
              <button
                type="button"
                onClick={handleClearMemory}
                disabled={isLoading}
                className="btn-glass btn-glass-red inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium"
                title="Wipe all Coach memory and start fresh"
              >
                <Trash2 size={12} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-md border border-transparent transition-[background-color,border-color,box-shadow,color] duration-150 ${
                darkMode
                  ? 'text-gray-400 hover:bg-white/[0.08] hover:border-white/20 hover:text-gray-200'
                  : 'text-gray-500 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-700'
              }`}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Body (scrollable) ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className={`mb-4 rounded-lg border-l-4 border-red-500 p-3 ${darkMode ? 'bg-red-500/10' : 'bg-red-50'}`}>
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                <strong>Build failed:</strong> {error}
              </p>
            </div>
          )}

          {!hasProfile && !isLoading && (
            <div className="text-center py-16">
              <div className={`inline-flex p-4 rounded-full border mb-4 ${
                darkMode
                  ? 'bg-purple-500/15 border-purple-400/40 ring-1 ring-inset ring-purple-400/20 shadow-[0_0_24px_rgba(168,85,247,0.18)]'
                  : 'bg-purple-50 border-purple-300 ring-1 ring-inset ring-purple-200'
              }`}>
                <Brain size={32} className={darkMode ? 'text-purple-200' : 'text-purple-700'} />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Coach has no memory of you yet
              </h3>
              <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Click <strong>Build Profile</strong> to scan every setup + ATM with enough trade history.
                Coach will identify your current edges, current leaks, and remember them so it can track
                drift over the coming weeks.
              </p>
              <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Coverage available: {setupCount} setup{setupCount === 1 ? '' : 's'}, {atmCount} ATM{atmCount === 1 ? '' : 's'}, {accountCount} account{accountCount === 1 ? '' : 's'}
              </p>
            </div>
          )}

          {hasProfile && (
            <>
              {/* ── Coach Health Score (Phase 14) — at-a-glance process health
                  Renders BEFORE Coach Voice so the user sees the headline
                  metric first, then the narrative explanation. */}
              {(() => {
                const health = computeHealthScore({ profile, history });
                return (
                  <div className="mb-6">
                    <CoachHealthScore health={health} darkMode={darkMode} />
                  </div>
                );
              })()}

              {/* ── Coach Suggestions (Phase 14) — one-click apply cards
                  Shows pending pin/add-rule/tag suggestions extracted from
                  the profile's observation actions[]. Sits BETWEEN health
                  + voice so the user can act on suggestions immediately
                  after seeing the health diagnosis. */}
              {coachSuggestions.length > 0 && (
                <div className="mb-6">
                  <CoachSuggestionsPanel
                    suggestions={coachSuggestions}
                    onApply={onApplyCoachSuggestion}
                    onDismiss={onDismissCoachSuggestion}
                    darkMode={darkMode}
                  />
                </div>
              )}

              {/* ── Coach Voice (LLM Narrator, Phase 3) ─────────────────
                  Renders Claude-generated coaching prose at the top of the
                  dashboard when the LLM narrator is enabled and an API key
                  is configured. Falls back gracefully:
                    - LLM disabled → section omitted entirely
                    - API errored  → small inline error, dashboard continues
                    - Loading      → animated placeholder so the layout
                                     doesn't jump when text arrives. */}
              {llmNarrator?.isReady && (
                <div className={`mb-6 rounded-xl border p-4 backdrop-blur-sm ${
                  darkMode
                    ? 'bg-purple-500/[0.08] border-purple-400/30 ring-1 ring-inset ring-purple-400/15 shadow-[0_0_18px_rgba(168,85,247,0.12)]'
                    : 'bg-purple-50/60 border-purple-200 ring-1 ring-inset ring-purple-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare size={14} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
                    <h3 className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
                      Coach Voice
                    </h3>
                    {narrative.loading && (
                      <Loader2 size={11} className={`animate-spin ${darkMode ? 'text-purple-300/70' : 'text-purple-700/70'}`} />
                    )}
                    {narrative.fromCache && !narrative.loading && (
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-px rounded-full ${darkMode ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                        cached
                      </span>
                    )}
                    {/* Phase 15 — TTS speak/stop button. Renders only when text exists
                        (not during loading) and TTS is enabled in Settings.
                        Phase 16 — threads ttsSettings so the provider dispatcher
                        knows whether to use browser / OpenAI / ElevenLabs. */}
                    {narrative.text && !narrative.loading && (
                      <CoachSpeakButton
                        text={narrative.text}
                        voice={llmNarrator?.settings?.voice || 'mentor'}
                        ttsEnabled={!!llmNarrator?.settings?.ttsEnabled}
                        ttsSettings={llmNarrator?.settings?.tts}
                        darkMode={darkMode}
                      />
                    )}
                  </div>
                  {narrative.loading && !narrative.text && (
                    <div className="space-y-1.5">
                      <div className={`h-3 w-full rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                      <div className={`h-3 w-11/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                      <div className={`h-3 w-9/12 rounded ${darkMode ? 'bg-white/5' : 'bg-gray-200'} animate-pulse`} />
                    </div>
                  )}
                  {!narrative.loading && narrative.text && (
                    <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      {narrative.text}
                    </p>
                  )}
                  {narrative.error && (
                    <p className={`text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                      Coach Voice unavailable: {narrative.error}. The dashboard below still works — fix the API key or model in Settings → AI Coach Narrator.
                    </p>
                  )}
                </div>
              )}

              {/* Theme tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatTile
                  icon={Sparkles}
                  label="Edges tracked"
                  value={profile.themes?.edgeCount || 0}
                  sublabel={profile.themes?.dominantEdge ? `Top: ${profile.themes.dominantEdge}` : 'None yet'}
                  accent="emerald"
                  darkMode={darkMode}
                />
                <StatTile
                  icon={AlertTriangle}
                  label="Leaks tracked"
                  value={profile.themes?.leakCount || 0}
                  sublabel={profile.themes?.dominantLeak ? `Top: ${profile.themes.dominantLeak}` : 'None'}
                  accent="amber"
                  darkMode={darkMode}
                />
                <StatTile
                  icon={CheckCircle2}
                  label="Resolved"
                  value={profile.themes?.resolvedCount || 0}
                  sublabel="Leaks gone since last snapshot"
                  accent="sky"
                  darkMode={darkMode}
                />
                {/* Hottest Scope tile — answers WHY this scope is hot, not just
                    what it is. Counts the edges + leaks anchored to this
                    scopeId from the already-loaded profile arrays so it
                    reflects the dashboard's own data without a schema bump.
                    Sublabel format: "N edges, M leaks here" — tells the
                    trader at a glance whether this scope is mostly working
                    (more edges) or mostly bleeding (more leaks). Tooltip
                    exposes the scope TYPE (setup / ATM / day / etc.) since
                    a bare ID can be ambiguous. */}
                {(() => {
                  const hotId = profile.themes?.hottestScopeId;
                  if (!hotId) {
                    return (
                      <StatTile
                        icon={Flame}
                        label="Hottest scope"
                        value="—"
                        sublabel="No scope dominates yet"
                        accent="purple"
                        darkMode={darkMode}
                      />
                    );
                  }
                  const edgesHere = (profile.edges || []).filter(e => e.scopeId === hotId).length;
                  const leaksHere = (profile.leaks || []).filter(e => e.scopeId === hotId).length;
                  const hotEntriesTotal = edgesHere + leaksHere;
                  // Scope type — same value across all entries with this
                  // scopeId since (scope, scopeId) uniquely identifies a
                  // surface. Pick any sample (first edge, fall back to first
                  // leak) to label.
                  const sample = (profile.edges || []).find(e => e.scopeId === hotId)
                    || (profile.leaks || []).find(e => e.scopeId === hotId);
                  const scopeType = sample?.scope || '';
                  const scopeTypeLabel = ({
                    setup: 'setup', atm: 'ATM', day: 'day', session: 'session', progress: 'progress',
                  })[scopeType] || scopeType;
                  const edgePart = `${edgesHere} edge${edgesHere === 1 ? '' : 's'}`;
                  const leakPart = `${leaksHere} leak${leaksHere === 1 ? '' : 's'}`;
                  const breakdown =
                    edgesHere > 0 && leaksHere > 0 ? `${edgePart}, ${leakPart} here`
                    : edgesHere > 0 ? `${edgePart} here`
                    : leaksHere > 0 ? `${leakPart} here`
                    : `${hotEntriesTotal} observations here`;
                  return (
                    <div title={`${scopeTypeLabel ? `${scopeTypeLabel} ` : ''}${hotId} — Coach has the most observations concentrated on this scope (${hotEntriesTotal} total: ${edgePart} + ${leakPart})`}>
                      <StatTile
                        icon={Flame}
                        label="Hottest scope"
                        value={hotId}
                        sublabel={breakdown}
                        accent="purple"
                        darkMode={darkMode}
                      />
                    </div>
                  );
                })()}
              </div>

              {/* Drift highlights — show NEW + ASCENDED + REGRESSED + RESOLVED */}
              {(newEdges.length > 0 || newLeaks.length > 0 || ascended.length > 0 || regressed.length > 0 || resolvedLeaks.length > 0) && (
                <Section
                  icon={Activity}
                  title="Since last snapshot"
                  accent="purple"
                  emptyMessage="—"
                  darkMode={darkMode}
                >
                  {/* Drift highlight cards — frosted tinted glass per severity.
                      Tints (emerald/red/amber/sky) match the SlicerBar pill
                      engaged-state language; each card gets a full border,
                      inset ring + a subtle color-keyed surface so the row
                      reads as a stack of identifiable glass chips instead of
                      flat colored slabs. */}
                  {ascended.map(e => (
                    <div key={`asc-${e.signature}`} className={`rounded-lg border p-2.5 transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-emerald-500/[0.08] border-emerald-400/40 ring-1 ring-inset ring-emerald-400/15'
                        : 'bg-emerald-50/70 border-emerald-200 ring-1 ring-inset ring-emerald-200'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <ArrowUpRight size={12} className={darkMode ? 'text-emerald-300' : 'text-emerald-600'} />
                        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-emerald-200/90' : 'text-emerald-700'}`}>
                          Ascended (leak → edge)
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{e.title}</p>
                    </div>
                  ))}
                  {regressed.map(e => (
                    <div key={`reg-${e.signature}`} className={`rounded-lg border p-2.5 transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-red-500/[0.08] border-red-400/40 ring-1 ring-inset ring-red-400/15'
                        : 'bg-red-50/70 border-red-200 ring-1 ring-inset ring-red-200'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <ArrowDownRight size={12} className={darkMode ? 'text-red-300' : 'text-red-600'} />
                        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-red-200/90' : 'text-red-700'}`}>
                          Regressed (edge → leak)
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{e.title}</p>
                    </div>
                  ))}
                  {newEdges.map(e => (
                    <div key={`ne-${e.signature}`} className={`rounded-lg border p-2.5 transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-emerald-500/[0.08] border-emerald-400/40 ring-1 ring-inset ring-emerald-400/15'
                        : 'bg-emerald-50/70 border-emerald-200 ring-1 ring-inset ring-emerald-200'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Sparkles size={12} className={darkMode ? 'text-emerald-300' : 'text-emerald-600'} />
                        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-emerald-200/90' : 'text-emerald-700'}`}>
                          New edge
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{e.title}</p>
                    </div>
                  ))}
                  {newLeaks.map(e => (
                    <div key={`nl-${e.signature}`} className={`rounded-lg border p-2.5 transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-amber-500/[0.08] border-amber-400/40 ring-1 ring-inset ring-amber-400/15'
                        : 'bg-amber-50/70 border-amber-200 ring-1 ring-inset ring-amber-200'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <AlertTriangle size={12} className={darkMode ? 'text-amber-300' : 'text-amber-600'} />
                        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-amber-200/90' : 'text-amber-700'}`}>
                          New leak
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{e.title}</p>
                    </div>
                  ))}
                  {resolvedLeaks.map(e => (
                    <div key={`res-${e.signature}`} className={`rounded-lg border p-2.5 transition-[background-color,border-color,box-shadow,color] duration-150 ${
                      darkMode
                        ? 'bg-sky-500/[0.08] border-sky-400/40 ring-1 ring-inset ring-sky-400/15'
                        : 'bg-sky-50/70 border-sky-200 ring-1 ring-inset ring-sky-200'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <CheckCircle2 size={12} className={darkMode ? 'text-sky-300' : 'text-sky-600'} />
                        <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-sky-200/90' : 'text-sky-700'}`}>
                          Resolved
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{e.title}</p>
                      <p className={`text-[11px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Watched for {e.occurrences || 1} run{e.occurrences === 1 ? '' : 's'} before going away.
                      </p>
                    </div>
                  ))}
                </Section>
              )}

              {/* Edges */}
              <Section
                icon={TrendingUp}
                title="Current edges"
                count={(profile.edges || []).length}
                accent="emerald"
                emptyMessage="No edges identified yet — Coach needs more data with successful patterns."
                darkMode={darkMode}
              >
                {(profile.edges || []).map(e => (
                  <ProfileEntryCard key={e.signature} entry={e} kind="edge" darkMode={darkMode} />
                ))}
              </Section>

              {/* Leaks */}
              <Section
                icon={TrendingDown}
                title="Current leaks"
                count={(profile.leaks || []).length}
                accent="amber"
                emptyMessage="No active leaks — clean run."
                darkMode={darkMode}
              >
                {(profile.leaks || []).map(e => (
                  <ProfileEntryCard key={e.signature} entry={e} kind="leak" darkMode={darkMode} />
                ))}
              </Section>

              {/* Resolved (full list, even if not in drift highlights) */}
              {resolvedLeaks.length > 0 && (
                <Section
                  icon={CheckCircle2}
                  title="Resolved leaks"
                  count={resolvedLeaks.length}
                  accent="sky"
                  emptyMessage=""
                  darkMode={darkMode}
                >
                  {resolvedLeaks.map(e => (
                    <ProfileEntryCard key={`r-${e.signature}`} entry={e} kind="resolved" darkMode={darkMode} />
                  ))}
                </Section>
              )}
            </>
          )}

          {hasProfile && (
            <div className={`mt-4 pt-4 border-t flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] ${darkMode ? 'border-white/5 text-gray-500' : 'border-gray-200 text-gray-500'}`}>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} />
                Profile spans the last {profile.windowDays} days · {profile.tradeCount} trades · schema v{profile.schemaVersion}
              </span>
              <span className="opacity-60">·</span>
              <span>
                Scanned {setupCount} setup{setupCount === 1 ? '' : 's'}, {atmCount} ATM{atmCount === 1 ? '' : 's'}, {accountCount} account{accountCount === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CoachDashboard;
